import { createContext, useContext, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase.ts';
import type { StaffMembership } from '../lib/types.ts';

interface StaffContextValue {
  memberships: StaffMembership[];
  activeMembership: StaffMembership | null;
  setActiveMembership: (m: StaffMembership) => void;
  isLoading: boolean;
}

const StaffContext = createContext<StaffContextValue | null>(null);

export function useStaff(): StaffContextValue {
  const ctx = useContext(StaffContext);
  if (!ctx) throw new Error('useStaff must be used inside StaffProvider');
  return ctx;
}

async function fetchStaffContext(): Promise<StaffMembership[]> {
  const { data, error } = await supabase.rpc('get_my_staff_context');
  if (error) throw error;
  const payload = data as { user_id: string; memberships: StaffMembership[] };
  return payload.memberships ?? [];
}

export function StaffProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [activeMembership, setActiveMembership] = useState<StaffMembership | null>(null);

  const { data: memberships = [], isLoading } = useQuery({
    queryKey: ['staff-context'],
    queryFn: fetchStaffContext,
    staleTime: 2 * 60_000,
    retry: false,
  });

  useEffect(() => {
    if (memberships.length > 0 && !activeMembership) {
      setActiveMembership(memberships[0]);
    }
  }, [memberships, activeMembership]);

  // Invalidate kitchen orders when membership changes.
  useEffect(() => {
    if (activeMembership) {
      queryClient.invalidateQueries({ queryKey: ['kitchen-orders'] });
    }
  }, [activeMembership, queryClient]);

  return (
    <StaffContext.Provider value={{ memberships, activeMembership, setActiveMembership, isLoading }}>
      {children}
    </StaffContext.Provider>
  );
}
